// src/pages/WarehousesPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { warehousesAPI, companiesAPI } from '../services/api';
import { Card, Badge, SectionHeader, EmptyState, Button, Modal, Input, ConfirmModal } from '../components/ui';
import toast from 'react-hot-toast';
import { Warehouse, Plus, Wifi, MapPin, Pencil, Trash2, Search } from 'lucide-react';
import styles from './WarehousesPage.module.css';

export default function WarehousesPage() {
    const { companyId, isGaafAdmin } = useAuth();
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    useEffect(() => {
        if (!isGaafAdmin) return;
        companiesAPI.list().then(res => {
            const list = res.data.companies || [];
            setCompanies(list);
            if (list.length > 0) setSelectedCompanyId(list[0].companyId);
        }).catch(() => { });
    }, [isGaafAdmin]);

    const effectiveCompanyId = isGaafAdmin ? selectedCompanyId : companyId;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await warehousesAPI.list(effectiveCompanyId || (isGaafAdmin ? undefined : companyId));
            setWarehouses((res.data.warehouses || []).filter(w => w.status !== 'inactive'));
        } catch (_) { }
        setLoading(false);
    }, [effectiveCompanyId, companyId, isGaafAdmin]);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            await warehousesAPI.delete(deleteTarget.warehouseId);
            toast.success('Warehouse Deactivated');
            setDeleteTarget(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to Deactivate Warehouse');
        }
        setDeleteLoading(false);
    };

    const filtered = warehouses.filter(w =>
        !search ||
        w.name?.toLowerCase().includes(search.toLowerCase()) ||
        w.city?.toLowerCase().includes(search.toLowerCase()) ||
        w.country?.toLowerCase().includes(search.toLowerCase()) ||
        w.readerConfig?.readerId?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className={`${styles.page} animate-fadeIn`}>
            <SectionHeader
                title="Warehouses"
                description="Manage Warehouse Locations and Reader Configurations"
                actions={
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        {isGaafAdmin && companies.length > 0 && (
                            <select value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)}
                                style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--text-primary)', padding: '7px 12px', fontFamily: 'var(--font-body)', fontSize: '0.875rem', outline: 'none' }}>
                                {companies.map(c => <option key={c.companyId} value={c.companyId}>{c.name}</option>)}
                            </select>
                        )}
                        <Button variant="primary" icon={<Plus size={15} />} onClick={() => setShowCreate(true)}>
                            Add Warehouse
                        </Button>
                    </div>
                }
            />

            <div style={{ position: 'relative', maxWidth: 360, marginBottom: 16 }}>
                <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none' }} />
                <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search Warehouses…"
                    style={{ width: '100%', paddingLeft: 34, paddingRight: 12, paddingTop: 8, paddingBottom: 8, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', color: 'var(--text-primary)', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }}
                />
            </div>

            {loading ? (
                <div className={styles.grid}>
                    {[1, 2].map(i => <div key={i} className={styles.skeleton} />)}
                </div>
            ) : filtered.length === 0 ? (
                <EmptyState
                    icon={<Warehouse size={40} />}
                    title={search ? 'No Warehouses match your search' : 'No Warehouses yet'}
                    description={search ? 'Try a different Search Term.' : 'Create a Warehouse then Provision an IoT Certificate for your Reader.'}
                    action={!search && <Button variant="primary" icon={<Plus size={15} />} onClick={() => setShowCreate(true)}>Add Warehouse</Button>}
                />
            ) : (
                <div className={styles.grid}>
                    {filtered.map(w => <WarehouseCard key={w.warehouseId} warehouse={w} onEdit={() => setEditTarget(w)} onDelete={() => setDeleteTarget(w)} />)}
                </div>
            )}

            <ConfirmModal
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                loading={deleteLoading}
                title="Deactivate Warehouse"
                message={`Deactivate "${deleteTarget?.name}"? The Warehouse will be marked Inactive. Existing Inventory Data is Preserved.`}
                confirmLabel="Deactivate"
            />

            <CreateWarehouseModal
                open={showCreate}
                companyId={effectiveCompanyId}
                onClose={() => setShowCreate(false)}
                onCreated={() => { setShowCreate(false); load(); }}
            />

            <CreateWarehouseModal
                open={!!editTarget}
                companyId={effectiveCompanyId}
                warehouse={editTarget}
                onClose={() => setEditTarget(null)}
                onCreated={() => { setEditTarget(null); load(); }}
            />
        </div>
    );
}

function WarehouseCard({ warehouse, onEdit, onDelete }) {
    const reader = warehouse.readerConfig;
    const readerStatus = warehouse.readerStatus;
    const isOnline = readerStatus?.status === 'online' &&
        readerStatus?.lastHeartbeat > Date.now() - 5 * 60 * 1000;

    return (
        <Card className={styles.card} hover>
            <div className={styles.cardHeader}>
                <div className={styles.whIcon}><Warehouse size={20} /></div>
                <div className={styles.whInfo}>
                    <h3 className={styles.whName}>{warehouse.name}</h3>
                    {(warehouse.city || warehouse.country) && (
                        <div className={styles.whLocation}>
                            <MapPin size={12} />
                            {[warehouse.city, warehouse.country].filter(Boolean).join(', ')}
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Badge variant={warehouse.status === 'active' ? 'success' : 'neutral'}>
                        {warehouse.status}
                    </Badge>
                    <button onClick={onEdit} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 4, borderRadius: 4, display: 'flex' }} title="Edit">
                        <Pencil size={14} />
                    </button>
                    <button onClick={onDelete} style={{ background: 'none', border: 'none', color: '#e07a7a', cursor: 'pointer', padding: 4, borderRadius: 4, display: 'flex' }} title="Deactivate">
                        <Trash2 size={14} />
                    </button>
                </div>
            </div>

            <div className={styles.readerSection}>
                <div className={styles.readerHeader}>
                    <Wifi size={14} />
                    <span>Reader</span>
                    <Badge variant={isOnline ? 'success' : 'neutral'} size="sm">
                        {isOnline ? 'Online' : 'Offline'}
                    </Badge>
                </div>
                <div className={styles.readerDetails}>
                    <div className={styles.rDetail}>
                        <span className={styles.rLabel}>Reader ID</span>
                        <code className={styles.rValue}>{reader?.readerId || 'Not Configured'}</code>
                    </div>
                    {readerStatus?.lastHeartbeat && (
                        <div className={styles.rDetail}>
                            <span className={styles.rLabel}>Last Heartbeat</span>
                            <span className={styles.rValue}>
                                {Math.round((Date.now() - readerStatus.lastHeartbeat) / 60000)}m ago
                            </span>
                        </div>
                    )}
                    {readerStatus?.firmware && (
                        <div className={styles.rDetail}>
                            <span className={styles.rLabel}>Firmware</span>
                            <span className={styles.rValue}>{readerStatus.firmware}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.antennaMap}>
                {Object.entries(reader?.antennaMap || {}).map(([port, position]) => (
                    <div key={port} className={styles.antenna}>
                        <span className={styles.antennaPort}>Port {port}</span>
                        <span className={styles.antennaPos}>{position.replace('_', ' ')}</span>
                    </div>
                ))}
            </div>

            <div className={styles.warehouseId}>
                <span className={styles.rLabel}>Warehouse ID</span>
                <code className={styles.rValue} style={{ fontSize: '0.7rem' }}>{warehouse.warehouseId}</code>
            </div>
        </Card>
    );
}

function CreateWarehouseModal({ open, companyId, warehouse, onClose, onCreated }) {
    const isEditing = !!warehouse;
    const [form, setForm] = useState({ name: '', city: '', country: 'Honduras', address: '', readerId: '' });
    const [loading, setLoading] = useState(false);
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    useEffect(() => {
        if (open) {
            if (warehouse) {
                setForm({
                    name: warehouse.name || '',
                    city: warehouse.city || '',
                    country: warehouse.country || 'Honduras',
                    address: warehouse.address || '',
                    readerId: warehouse.readerConfig?.readerId || '',
                });
            } else {
                setForm({ name: '', city: '', country: 'Honduras', address: '', readerId: '' });
            }
            setLoading(false);
        }
    }, [open, warehouse?.warehouseId]);

    const handleSubmit = async () => {
        if (!form.name) return toast.error('Warehouse Name is Required');
        setLoading(true);
        try {
            if (isEditing) {
                await warehousesAPI.update(warehouse.warehouseId, {
                    name: form.name, city: form.city, country: form.country, address: form.address,
                    readerConfig: { ...warehouse.readerConfig, readerId: form.readerId || null },
                });
                toast.success('Warehouse Updated');
            } else {
                await warehousesAPI.create({
                    companyId, name: form.name, city: form.city, country: form.country, address: form.address,
                    readerConfig: { readerId: form.readerId || null, antennaMap: { 1: 'INSIDE_LEFT', 2: 'INSIDE_RIGHT', 3: 'OUTSIDE_LEFT', 4: 'OUTSIDE_RIGHT' } },
                });
                toast.success('Warehouse Created');
            }
            onCreated();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to Create Warehouse');
        }
        setLoading(false);
    };

    return (
        <Modal open={open} onClose={onClose} title={isEditing ? `Edit — ${warehouse?.name}` : "Add Warehouse"}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Input label="Warehouse Name *" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Main Warehouse" />
                <Input label="City" value={form.city} onChange={e => set('city', e.target.value)} placeholder="Jalapa" />
                <Input label="Country" value={form.country} onChange={e => set('country', e.target.value)} placeholder="Honduras" />
                <Input label="Address" value={form.address} onChange={e => set('address', e.target.value)} placeholder="Km 5 Carretera..." />
                <Input label="Reader ID (Optional)" value={form.readerId} onChange={e => set('readerId', e.target.value)} placeholder="r420-gaaf-001" hint="Can be set later when Configuring the Reader" />
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid var(--border-subtle)' }}>
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" loading={loading} onClick={handleSubmit}>Create Warehouse</Button>
                </div>
            </div>
        </Modal>
    );
}
